import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Users, Award, ChefHat } from 'lucide-react';
import { images } from '../../assets/images';

const AboutSection: React.FC = () => {

  const features = [
    {
      icon: Heart,
      title: 'Paixão pela Gastronomia',
      description: 'Cada prato é preparado com amor e dedicação, transformando ingredientes em experiências únicas.'
    },
    {
      icon: Users,
      title: 'Equipe Especializada',
      description: 'Profissionais experientes e apaixonados pela arte culinária, sempre prontos para superar expectativas.'
    },
    {
      icon: Award,
      title: 'Excelência Comprovada',
      description: 'Mais de 20 anos de experiência em alta gastronomia, garantindo qualidade e sofisticação em cada evento.'
    },
    {
      icon: ChefHat,
      title: 'Ingredientes Exclusivos',
      description: 'Selecionamos os melhores ingredientes para criar receitas únicas e memoráveis.'
    }
  ];

  return (
    <section id="about" className="section-padding bg-gradient-to-br from-gray-50 to-white">
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-playfair font-bold text-gray-900 mb-6">
            Quem <span className="text-gradient">Somos</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-600 to-accent-600 mx-auto rounded-full"></div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center mb-12 sm:mb-16 lg:mb-20">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <div className="space-y-3 sm:space-y-4">
              <h3 className="text-xl sm:text-2xl font-playfair font-semibold text-gray-900">
                Nossa História
              </h3>
              <p className="text-gray-600 leading-relaxed text-base sm:text-lg">
                Da união de três amigos com objetivos comuns e muita vontade de trazer o melhor em gastronomia surge o: 
                <span className="font-semibold text-primary-600"> "Buffet Impetus"</span>.
              </p>
            </div>

            <div className="space-y-3 sm:space-y-4">
              <p className="text-gray-600 leading-relaxed text-base sm:text-lg">
                Com mais de 20 anos de experiência em alta gastronomia com excelência e sofisticação, combinamos 
                ingredientes exclusivos em uma única receita de sucesso. De maneira atraente e personalizada, 
                cada evento se torna único.
              </p>
            </div>

            {/* Video Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="mt-8"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <video
                  className="w-full aspect-video object-cover"
                  controls
                  preload="metadata"
                  poster={images.about}
                  playsInline
                  style={{ backgroundColor: '#000' }}
                >
                  <source src="/assets/video/impetusgastronomia.mp4" type="video/mp4" />
                  <source src="/assets/video/impetusvideoreel.webm" type="video/webm" />
                  <source src="/assets/video/impetusvideoreel.mp4" type="video/mp4" />
                  <p className="text-center text-gray-600 p-4 bg-white">
                    Seu navegador não suporta vídeos HTML5. 
                    <br />
                    <a 
                      href="/assets/video/impetusgastronomia.mp4" 
                      className="text-primary-600 hover:text-primary-700 underline"
                      target="_blank"
                    >
                      Clique aqui para abrir o vídeo
                    </a>
                  </p>
                </video>
              </div>
            </motion.div>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={images.about}
                alt="Sobre a Impetus Gastronomia"
                className="w-full h-[300px] sm:h-[400px] lg:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
            </div>
            
            {/* Floating Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="absolute -bottom-4 sm:-bottom-6 -left-4 sm:-left-6 bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-xl"
            >
              <div className="flex items-center space-x-3 sm:space-x-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary-600 to-accent-600 rounded-full flex items-center justify-center">
                  <Award className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm sm:text-base">20+ Anos</h4>
                  <p className="text-xs sm:text-sm text-gray-600">de experiência</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8"
        >
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="text-center group"
            >
              <motion.div
                whileHover={{ scale: 1.1, rotate: 5 }}
                className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-primary-600 to-accent-600 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4 group-hover:shadow-lg transition-shadow duration-300"
              >
                <feature.icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
              </motion.div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">
                {feature.title}
              </h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;
